// Hook that captures the browser beforeinstallprompt event so the app can
// show its own install button instead of relying on Chrome's engagement-gated
// mini-infobar (which can take multiple visits before it ever appears).
//
// Usage:
//   const { canInstall, install } = useInstallPrompt();
//   canInstall  - true when the browser is ready to install and we are not
//                 already running as a standalone PWA
//   install()   - call when the user clicks the install button; shows the
//                 native install dialog and resolves when the user decides

import { useEffect, useState } from "react";

// The beforeinstallprompt Event is not in the standard lib typings yet.
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: "accepted" | "dismissed";
    platform: string;
  }>;
  prompt(): Promise<void>;
}

export function useInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(
    () => window.matchMedia("(display-mode: standalone)").matches,
  );

  useEffect(() => {
    // If already installed, no need to setup listeners
    if (isInstalled) return;

    const handleBeforeInstallPrompt = (e: Event) => {
      // Prevent Chrome showing its own mini-infobar so we control the UX.
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt,
      );
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, [isInstalled]);

  const install = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      setDeferredPrompt(null);
      setIsInstalled(true);
    }
  };

  return {
    // True only when the browser has fired beforeinstallprompt and we are
    // not already installed / running standalone.
    canInstall: !!deferredPrompt && !isInstalled,
    install,
  };
}
